'use client';
import Image from 'next/image';
import React from 'react';
import { FormValidationMessage } from '../FormValidationMessage';
import VisibilityOffIconImage from './visibility_off.svg?url';
import VisibilityIconImage from './visibility_on.svg?url';

type Attributes = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'inputMode'
> & {
  inputMode?:
    | 'search'
    | 'text'
    | 'email'
    | 'url'
    | 'none'
    | 'tel'
    | 'numeric'
    | 'decimal'
    | 'latin';
};

export const FormInputText: React.FC<
  {
    label: string;
    validationMessage?: string | undefined;
    isValidationError?: boolean; // for red border without error message
    ref?: React.Ref<HTMLInputElement>;
  } & Attributes
> = ({
  label,
  validationMessage,
  isValidationError = false,
  type = 'text',
  placeholder = ' ',
  ref,
  ...attr
}) => {
  const isPassword = type === 'password';
  const [isRevealPassword, setIsRevealPassword] =
    React.useState<boolean>(false);

  return (
    <div>
      <div
        className={
          validationMessage || isValidationError
            ? 'relative overflow-hidden rounded-2 border-2 border-caution-primary'
            : 'relative overflow-hidden rounded-2 border-2 border-main-10 focus-within:border-main-40'
        }
      >
        <div className="absolute inset-0 bg-base-100" />
        <input
          {...(attr as React.InputHTMLAttributes<HTMLInputElement>)}
          ref={ref}
          placeholder={placeholder}
          type={isPassword && isRevealPassword ? 'text' : type}
          className="peer relative block h-14 w-full appearance-none bg-base-100 px-4 pt-5 font-[600] text-main-100 outline-hidden placeholder:font-normal placeholder:text-main-40 placeholder:opacity-0 placeholder:transition-opacity placeholder:ease-linear placeholder:cst-halt focus:placeholder:opacity-100"
        />
        <label className="pointer-events-none absolute inset-y-0 left-4 right-8 my-auto block h-[1em] origin-[0] truncate text-base2 font-[600] leading-[1] text-main-80 transition-transform cst-halt peer-focus:-translate-y-4 peer-focus:scale-[85%] peer-focus:text-main-80 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-[85%] peer-[:not(:placeholder-shown)]:text-main-80">
          {label}
        </label>
        {isPassword && (
          <div
            tabIndex={0}
            role="button"
            className="absolute inset-y-0 right-4 my-auto size-8 cursor-pointer opacity-80"
            onClick={() => {
              setIsRevealPassword(!isRevealPassword);
            }}
          >
            {isRevealPassword ? (
              <Image src={VisibilityIconImage} alt="" className="size-full" />
            ) : (
              <Image
                src={VisibilityOffIconImage}
                alt=""
                className="size-full"
              />
            )}
          </div>
        )}
      </div>
      {validationMessage && (
        <FormValidationMessage validateMessage={validationMessage} />
      )}
    </div>
  );
};
